import moment from 'moment';
import { IMultiIcon } from './types';

interface IDateComparator {
    dateFormat: string;
    first: string;
    second: string;
}

const extractValue = (input: any): { value: any; type: string } => {
    if (input && typeof input === 'object' && 'value' in input) {
        return {
            value: input.value,
            type: typeof input.value,
        };
    }

    return {
        value: input,
        type: typeof input,
    };
};

export const dateComparator = ({
    dateFormat,
    first,
    second,
}: IDateComparator) => {
    if (first === '' && second === '') {
        return 0;
    }
    if (first === '') {
        return -1;
    }
    if (second === '') {
        return 1;
    }
    const firstDateMoment = moment(first, dateFormat);
    const secondDateMoment = moment(second, dateFormat);

    return firstDateMoment.diff(secondDateMoment);
};

const number = ({
    first,
    second,
}: {
    first?: number | number[];
    second?: number | number[];
}) => {
    const getValue = (val?: number | number[]) =>
        Array.isArray(val) ? val[0] : val;

    const firstValue = getValue(first);
    const secondValue = getValue(second);

    if (firstValue === undefined && secondValue === undefined) {
        return 0;
    }
    if (firstValue === undefined) {
        return -1;
    }
    if (secondValue === undefined) {
        return 1;
    }

    return firstValue - secondValue;
};

interface IMultiIconComparator {
    first?: IMultiIcon[];
    second?: IMultiIcon[];
}

const multiIcon = ({ first, second }: IMultiIconComparator) => {
    if (first === undefined && second === undefined) {
        return 0;
    }
    if (!first) {
        return -1;
    }
    if (!second) {
        return 1;
    }

    if (first.length > second.length) {
        return 1;
    }
    if (first.length < second.length) {
        return -1;
    }

    for (let i = 0; i < first.length; i++) {
        const compareResult = first[i].name.localeCompare(second[i].name);
        if (compareResult !== 0) {
            return compareResult;
        }
    }

    return 0;
};

const comparatorTypes = {
    date: dateComparator,
    multiIcon,
    number,
    duration: number,
    bar: number,
};

const defaultComparator = (first?: string, second?: string) => {
    const isFirstEmpty = first === '' || first === undefined || first === null;
    const isSecondEmpty =
        second === '' || second === undefined || second === null;
    if (isFirstEmpty && isSecondEmpty) {
        return 0;
    }
    if (isFirstEmpty) {
        return -1;
    }
    if (isSecondEmpty) {
        return 1;
    }

    const lowerFirst = String(first)?.toLowerCase() || '';
    const lowerSecond = String(second)?.toLowerCase() || '';
    return lowerFirst.localeCompare(lowerSecond);
};

interface IComparatorArgs {
    dateFormat: string;
    type: string;
}

export default ({ dateFormat, type }: IComparatorArgs) =>
    (first: any, second: any) => {
        const firstExtracted = extractValue(first);
        const secondExtracted = extractValue(second);

        const { value: val1, type: val1Type } = firstExtracted;
        const { value: val2, type: val2Type } = secondExtracted;

        if (val1Type === 'number' && val2Type === 'number') {
            return val1 - val2;
        }

        return comparatorTypes[type]
            ? comparatorTypes[type]({ dateFormat, first: val1, second: val2 })
            : defaultComparator(val1, val2);
    };
